import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { rolesApiService } from '../api/roles-api';
import { Role, RoleOption, RoleFilters } from '../types/role-types';

interface RolesState {
  roles: Role[];
  businessAdminRoles: RoleOption[];
  currentRole: Role | null;
  loading: boolean;
  error: string | null;
  filters: RoleFilters;
  lastFetch: number | null;
}

const initialState: RolesState = {
  roles: [],
  businessAdminRoles: [],
  currentRole: null,
  loading: false,
  error: null,
  filters: {},
  lastFetch: null,
};

// Async thunks
export const fetchRoles = createAsyncThunk(
  'roles/fetchRoles',
  async (filters: RoleFilters = {}, { rejectWithValue }) => {
    try {
      const roles = await rolesApiService.getRoles(filters);
      return { roles, filters };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar roles'
      );
    }
  }
);

export const fetchBusinessAdminRoles = createAsyncThunk(
  'roles/fetchBusinessAdminRoles',
  async (_, { rejectWithValue }) => {
    try {
      const roles = await rolesApiService.getBusinessAdminRoles();
      return roles;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar roles de negocio'
      );
    }
  }
);

export const fetchRoleOptions = createAsyncThunk(
  'roles/fetchRoleOptions',
  async (filters: RoleFilters = {}, { rejectWithValue }) => {
    try {
      const options = await rolesApiService.getRoleOptions(filters);
      return { options, filters };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar opciones de roles'
      );
    }
  }
);

export const fetchRoleById = createAsyncThunk(
  'roles/fetchRoleById',
  async (id: string, { rejectWithValue }) => {
    try {
      const role = await rolesApiService.getRoleById(id);
      return role;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar rol'
      );
    }
  }
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setCurrentRole: (state, action: PayloadAction<Role | null>) => {
      state.currentRole = action.payload;
    },
    setFilters: (state, action: PayloadAction<RoleFilters>) => {
      state.filters = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    clearRoles: state => {
      state.roles = [];
      state.businessAdminRoles = [];
      state.currentRole = null;
      state.error = null;
      state.lastFetch = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch roles
      .addCase(fetchRoles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.roles;
        state.filters = action.payload.filters;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch business admin roles
      .addCase(fetchBusinessAdminRoles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessAdminRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.businessAdminRoles = action.payload;
        state.error = null;
      })
      .addCase(fetchBusinessAdminRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch role options
      .addCase(fetchRoleOptions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.options.map(option => ({
          id: option.value,
          code: option.value as any,
          name: option.label,
          description: option.description,
          category: option.category,
          isActive: true,
          isAdmin: option.isAdmin,
          sortOrder: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        }));
        state.filters = action.payload.filters;
        state.error = null;
      })
      .addCase(fetchRoleOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch role by ID
      .addCase(fetchRoleById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRole = action.payload;
        state.error = null;
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentRole, setFilters, clearError, clearRoles } =
  rolesSlice.actions;
export default rolesSlice.reducer;
